import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@/redux/hooks/hooks";
import {
  COMPLETION_RULES,
  computeProfileCompletion,
  pickEmoji,
} from "@/utils/profileCompletion";

type Props = {
  open: boolean;
  onClose: () => void;
  currentStep: number;
};

type RuleWithStatus = (typeof COMPLETION_RULES)[number] & {
  completed: boolean;
};

function labelFromId(id: string) {
  const map: Record<string, string> = {
    fullName: "Full name",
    gender: "Gender",
    ageGroup: "Age group",
    bodyType: "Body type",
    skinTone: "Skin tone",
    profilePhoto: "Profile photo",

    phoneNumber: "Phone number",
    address: "Address",
    country: "Country",
    state: "State",
    city: "City",
    pincode: "Pincode",
    mapLink: "Map link",

    skillsMulti: "Skills",
    profileLinks: "Profile links",
    comfortableWith: "Comfortable with",
    portfolioVideos: "Portfolio videos",
    availabilityPerWeek: "Availability per week",
    weekendAvailabilityV2: "Weekend availability",

    coreSetup: "Core setup",
    whoCanAppear: "Who can appear",

    language: "Language",
  };

  return map[id] ?? id;
}

function groupForRule(id: string) {
  // matches your rule list order
  if (
    [
      "fullName",
      "gender",
      "ageGroup",
      "bodyType",
      "skinTone",
      "profilePhoto",
    ].includes(id)
  )
    return "Basics";
  if (
    [
      "phoneNumber",
      "address",
      "country",
      "state",
      "city",
      "pincode",
      "mapLink",
    ].includes(id)
  )
    return "Contact";
  if (
    [
      "skillsMulti",
      "profileLinks",
      "comfortableWith",
      "portfolioVideos",
      "availabilityPerWeek",
      "weekendAvailabilityV2",
    ].includes(id)
  )
    return "Portfolio";
  if (["coreSetup", "whoCanAppear"].includes(id)) return "Equipment";
  return "Other";
}

function cn(...arr: Array<string | false | null | undefined>) {
  return arr.filter(Boolean).join(" ");
}

export default function ProfileCompletionModal({
  open,
  onClose,
  currentStep,
}: Props) {
  const slice = useAppSelector((s) => s.signUpV2);

  const { percent, done, total } = useMemo(
    () => computeProfileCompletion(slice),
    [slice],
  );

  const emoji = useMemo(
    () => pickEmoji(percent, currentStep),
    [percent, currentStep],
  );

  const rules: RuleWithStatus[] = useMemo(
    () =>
      COMPLETION_RULES.map((r) => ({
        ...r,
        completed: r.check(slice),
      })),
    [slice],
  );

  const grouped = useMemo(() => {
    const groups: Record<string, RuleWithStatus[]> = {};
    for (const r of rules) {
      const g = groupForRule(r.id);
      groups[g] = groups[g] || [];
      groups[g].push(r);
    }
    return groups;
  }, [rules]);

  const completedCount = rules.filter((r) => r.completed).length;

  // mount for smooth exit animation
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  // close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[80] lg:hidden">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close modal"
        onClick={onClose}
        className={cn(
          "absolute inset-0 bg-black/45",
          "transition-opacity duration-300 ease-out",
          open ? "opacity-100" : "opacity-0",
        )}
      />

      {/* Bottom sheet panel */}
      <div
        className={cn(
          "absolute left-0 right-0 bottom-0",
          "rounded-t-3xl bg-white",
          "shadow-[0_-20px_60px_rgba(0,0,0,0.25)]",
          "border-t border-black/10",
          "transition-all duration-300 ease-out",
          open ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
        )}
        onTransitionEnd={() => {
          if (!open) setMounted(false);
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* Handle */}
        <div className="pt-3 flex justify-center">
          <div className="h-1.5 w-10 rounded-full bg-black/10" />
        </div>

        {/* Header */}
        <div className="px-5 pt-3 pb-4 border-b border-black/10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[#060B13] font-semibold text-base tracking-[-0.7px]">
                Profile completion {emoji}
              </p>
              <p className="text-[12px] text-black/55 mt-1">
                {percent}% • {completedCount}/{rules.length} fields • score{" "}
                {done}/{total}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold bg-black text-white"
            >
              Close
            </button>
          </div>

          {/* Progress bar (your style) */}
          <div className="mt-3 h-[7px] w-full rounded-[21px] bg-[#FF66001A] overflow-hidden">
            <div
              className="h-full rounded-[21px] bg-[#FF6600] transition-[width] duration-700 ease-out will-change-[width]"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-4 max-h-[70vh] overflow-auto">
          {Object.entries(grouped).map(([groupName, items]) => {
            const doneInGroup = items.filter((i) => i.completed).length;

            return (
              <div key={groupName} className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-[#060B13]">
                    {groupName}
                  </p>
                  <span className="text-[12px] text-black/55">
                    {doneInGroup}/{items.length}
                  </span>
                </div>

                <div className="space-y-2">
                  {items.map((it) => (
                    <div
                      key={it.id}
                      className={cn(
                        "rounded-2xl border p-3 flex items-center justify-between gap-3",
                        it.completed
                          ? "border-black/10 bg-black/[0.02]"
                          : "border-black/10 bg-white",
                      )}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#060B13] truncate">
                          {labelFromId(it.id)}
                        </p>
                        <p className="text-[12px] text-black/45">
                          Weight: {it.weight}
                        </p>
                      </div>

                      <span
                        className={cn(
                          "text-[12px] font-semibold px-2.5 py-1 rounded-full shrink-0",
                          it.completed
                            ? "bg-black text-white"
                            : "bg-[#FFF4EC] text-[#FF6600] border border-[#FF660033]",
                        )}
                      >
                        {it.completed ? "Done" : "Missing"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="mt-4 text-[12px] text-black/55">
            Tip: complete the <span className="font-semibold">Missing</span>{" "}
            fields to boost visibility.
          </div>
        </div>

        {/* Footer action (optional) */}
        <div className="px-5 pb-5">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-2xl py-3 font-semibold bg-[#FF6600] text-white"
          >
            Continue filling profile
          </button>
        </div>
      </div>
    </div>
  );
}
